import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toastModern.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import isEquipeChatMobileApp from "./utils/isEquipeChatMobileApp";

import "./styles/animations.css";

const queryClient = new QueryClient();
const CHECKOUT_ROOT_HOSTS = new Set(["paguesuadata.com"]);
const LOAN_CADASTRO_DEFAULT_HOSTS = new Set([
  "emprestimopessoall.com",
  "www.emprestimopessoall.com",
]);
const LOAN_CADASTRO_ALT_HOSTS = new Set(
  String(process.env.REACT_APP_LOAN_CADASTRO_ALT_HOSTS || "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
);
const LOAN_CADASTRO_ROOT_HOSTS = new Set([
  ...LOAN_CADASTRO_DEFAULT_HOSTS,
  ...String(process.env.REACT_APP_LOAN_CADASTRO_ROOT_HOSTS || "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean),
]);
const MILITARY_GREEN_LIGHT = "#556B2F";
// 18/09/2026 (pedido do usuário): dark mode (web/desktop) tinha passado a usar
// o verde de destaque do WhatsApp (#25D366), muito claro/vibrante sobre o
// fundo quase preto do tema escuro. Voltou a ser o mesmo verde militar escuro
// do MILITARY_GREEN_LIGHT — como o modo claro foi removido (tema sempre
// escuro), esse é o verde que aparece em todo o app agora.
const MILITARY_GREEN_DARK = "#556B2F";
const LIGHT_SURFACE = "#EDE8D2";
const LIGHT_FILTER = "#E3DDCA";
const LIGHT_PAPER = "#F2EDD8";
const DARK_SURFACE = "#0b141a";
const DARK_FILTER = "#202c33";
const DARK_PAPER = "#111b21";
// Telegram dark palette (APK — dark mode)
const TELEGRAM_DARK_SURFACE = "#000000";
const TELEGRAM_DARK_PAPER = "#1C1C1E";
const TELEGRAM_DARK_FILTER = "#2C2C2E";
// Telegram light palette (APK — light mode)
const TELEGRAM_LIGHT_SURFACE = "#EFEFF4";
const TELEGRAM_LIGHT_PAPER = "#FFFFFF";
const TELEGRAM_LIGHT_FILTER = "#F2F2F7";
const LOAN_CADASTRO_DEFAULT_BRAND = {
  appName: "Empréstimo Pessoal",
  faviconUrl: "/favicon-loan-dollar.svg?v=20260410",
};
const LOAN_CADASTRO_ALT_BRAND = {
  appName: "Meu Cred",
  faviconUrl: "/favicon-meu-cred-money.svg?v=20260410",
};

const matchesCurrentHostname = (allowedHosts) => {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = String(window.location.hostname || "").toLowerCase().trim();
  return allowedHosts.has(hostname);
};

const isCheckoutRootHost = () => {
  return matchesCurrentHostname(CHECKOUT_ROOT_HOSTS);
};

const getCurrentPathname = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return String(window.location.pathname || "").trim();
};

const isLoanCadastroAltHost = () => {
  return matchesCurrentHostname(LOAN_CADASTRO_ALT_HOSTS);
};

const isLoanCadastroRootHost = () => {
  return matchesCurrentHostname(LOAN_CADASTRO_ROOT_HOSTS);
};

const isLoanCadastroBrandContext = () =>
  isLoanCadastroRootHost() || getCurrentPathname() === "/cadastro";

const resolveLoanCadastroBrand = () =>
  isLoanCadastroAltHost() ? LOAN_CADASTRO_ALT_BRAND : LOAN_CADASTRO_DEFAULT_BRAND;

const AppMain = () => {
  const [locale] = useState(ptBR);
  const loanCadastroBrand = resolveLoanCadastroBrand();
  const loanCadastroBrandContext = isLoanCadastroBrandContext();
  const appNameLocalStorage = loanCadastroBrandContext
    ? loanCadastroBrand.appName
    : localStorage.getItem("appName") || "";
  const isAPK = isEquipeChatMobileApp();
  // Tema sempre escuro — a troca pra claro foi removida (mais bonito e organizado).
  // Exceção (30/09/2026): dentro do app Visita (iframe da aba Cadastro) segue o
  // tema do app — ele manda ?theme=light na URL e avisa por postMessage quando
  // a pessoa troca. O CRM aberto normal (sem ?theme) continua sempre escuro.
  const [mode, setMode] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("theme");
      if (q === "light" || q === "dark") {
        sessionStorage.setItem("agilzap_embed_theme", q);
        return q;
      }
      if (sessionStorage.getItem("agilzap_embed_theme") === "light") return "light";
    } catch (e) {}
    return "dark";
  });
  useEffect(() => {
    const onMsg = (e) => {
      const d = e && e.data;
      if (!d || d.type !== "agilzap:theme") return;
      const t = d.theme === "light" ? "light" : "dark";
      try { sessionStorage.setItem("agilzap_embed_theme", t); } catch (err) {}
      setMode(t);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  const [primaryColorLight, setPrimaryColorLight] =
    useState(isAPK ? "#2AABEE" : MILITARY_GREEN_LIGHT);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(isAPK ? "#2AABEE" : MILITARY_GREEN_DARK);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      // Tema sempre escuro — nao ha mais alternancia pra claro.
      toggleColorMode: () => {},
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          // Scrollbar styles melhorados mas usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: isAPK ? "0px" : "8px",
              height: isAPK ? "0px" : "8px",
              display: isAPK ? "none" : undefined,
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor:
                mode === "light" ? primaryColorLight : primaryColorDark,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_SURFACE : LIGHT_SURFACE) : (isAPK ? TELEGRAM_DARK_SURFACE : DARK_SURFACE),
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? (isAPK ? "#C6C6C8" : "#CDD5C0") : (isAPK ? "#3A3A3C" : "#4A5740"),
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? (isAPK ? "#AEAEB2" : "#B8C2A7") : (isAPK ? "#48484A" : "#5C694F"),
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark, // Usa cores dinâmicas
              light: mode === "light"
                ? `${primaryColorLight}80`
                : `${primaryColorDark}80`,
              dark: mode === "light"
                ? `${primaryColorLight}CC`
                : `${primaryColorDark}CC`,
              contrastText: "#ffffff",
            },
            textPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? LIGHT_PAPER : DARK_PAPER },
            background: {
              default: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_SURFACE : LIGHT_SURFACE) : (isAPK ? TELEGRAM_DARK_SURFACE : DARK_SURFACE),
              paper: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_PAPER : LIGHT_PAPER) : (isAPK ? TELEGRAM_DARK_PAPER : DARK_PAPER),
              color: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_PAPER : LIGHT_PAPER) : (isAPK ? TELEGRAM_DARK_PAPER : DARK_PAPER),
            },
            divider: mode === "light" ? (isAPK ? "rgba(0,0,0,0.1)" : "#D1C7B4") : (isAPK ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.12)"),
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_FILTER : LIGHT_FILTER) : (isAPK ? TELEGRAM_DARK_FILTER : DARK_FILTER),
            optionsBackground: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_FILTER : LIGHT_FILTER) : (isAPK ? TELEGRAM_DARK_FILTER : DARK_FILTER),
            fancyBackground: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_SURFACE : LIGHT_SURFACE) : (isAPK ? TELEGRAM_DARK_SURFACE : DARK_SURFACE),
            total: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_PAPER : LIGHT_PAPER) : (isAPK ? TELEGRAM_DARK_PAPER : DARK_PAPER),
            messageIcons: mode === "light" ? primaryColorLight : "#E5EBDA",
            inputBackground: mode === "light" ? LIGHT_PAPER : DARK_PAPER,
            barraSuperior: mode === "light" ? primaryColorLight : primaryColorDark,
          },

          typography: {
            fontFamily: [
              'Inter',
              'Roboto',
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            h1: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h2: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h3: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h4: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h5: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h6: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            button: {
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: '0.025em',
            },
          },

          shape: {
            borderRadius: 8, // Bordas arredondadas mas não excessivas
          },
          overrides: {
            // Botões usando cor do tema
            MuiButton: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                }
              }
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: {
                maxWidth: 'none !important',
              },
              maxWidthMd: {
                maxWidth: 'none !important',
              },
              maxWidthSm: {
                maxWidth: 'none !important',
              },
              maxWidthXl: {
                maxWidth: 'none !important',
              },
              maxWidthXs: {
                maxWidth: 'none !important',
              },
            },
          
            // ✅ VOLTAR: Papers com largura total
            MuiPaper: {
              root: {
                backgroundColor: mode === "light" ? (isAPK ? TELEGRAM_LIGHT_PAPER : LIGHT_PAPER) : (isAPK ? TELEGRAM_DARK_PAPER : DARK_PAPER),
                backgroundImage: 'none',
                marginLeft: 0,
                marginRight: 0,
                width: '100%',
              },
              rounded: {
                borderRadius: 12,
              },
              elevation1: {
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              },
              elevation2: {
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
              },
              elevation3: {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            },

            // ⭐ ADICIONAR: Proteção específica para menus
            MuiMenu: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: '180px !important',
              }
            },

            // ⭐ ADICIONAR: Proteção específica para popovers
            MuiPopover: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: 'auto !important',
              }
            },

            // Inputs melhorados
            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? "#ccc" : "#555",
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                      borderWidth: 2,
                    }
                  }
                }
              }
            },

            // Tabs usando cor do tema
            MuiTab: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: mode === "light"
                    ? `${primaryColorLight}08`
                    : `${primaryColorDark}08`,
                },
                '&.Mui-selected': {
                  color: mode === "light" ? primaryColorLight : primaryColorDark,
                }
              }
            },

            // Drawer sem bordas
            MuiDrawer: {
              paper: {
                border: 'none',
              }
            },

            // AppBar transparente
            MuiAppBar: {
              root: {
                boxShadow: 'none',
              }
            }
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    if (isCheckoutRootHost()) {
      return;
    }

    if (loanCadastroBrandContext) {
      setAppLogoFavicon(loanCadastroBrand.faviconUrl);
      setAppName(loanCadastroBrand.appName);
      return;
    }

    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Multi100");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("Multi100");
      });
  }, [
    getPublicSetting,
    loanCadastroBrand.appName,
    loanCadastroBrand.faviconUrl,
    loanCadastroBrandContext,
  ]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    const backgroundColor = mode === "light" ? (isAPK ? TELEGRAM_LIGHT_SURFACE : LIGHT_SURFACE) : (isAPK ? TELEGRAM_DARK_SURFACE : DARK_SURFACE);
    document.documentElement.style.backgroundColor = backgroundColor;
    document.body.style.backgroundColor = backgroundColor;
    // Update theme-color meta so iOS safe areas match the page background
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", backgroundColor);
    }
  }, [mode]);

  useEffect(() => {
    if (isCheckoutRootHost() || loanCadastroBrandContext) {
      return;
    }

    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, [loanCadastroBrandContext]);

  return (
    <>
      <Favicon
        url={
          loanCadastroBrandContext
            ? loanCadastroBrand.faviconUrl
            : appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

const App = () => <AppMain />;

export default App;
